import React, { Children, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { range, ceil } from 'lodash';

import { Text, Paragraph } from 'components/core/Typography';

import StarButton from './StarButton';

import './StarInput.scss';

export function StarInput({
	className,
	halfStep,
	input,
	value: valueProp,
	onChange: onChangeProp,
	size,
	helpTexts,
	label,
	error,
	totalStars = 5,
}) {
	const value = input?.value ?? valueProp ?? '';
	const onChange = input?.onChange ?? onChangeProp;

	const stars = useMemo(() => {
		const numElements = halfStep ? totalStars * 2 : totalStars;
		return range(1, numElements + 1).map((index) =>
			halfStep ? index / 2 : index,
		);
	}, [halfStep, totalStars]);

	const onSelect = (val) => onChange(`${val}`);

	const [activeRating, setActiveRating] = useState(Number.parseFloat(value));
	const onMouseEnter = (val) => setActiveRating(val);
	const onMouseLeave = () => setActiveRating(Number.parseFloat(value));

	const helpText = useMemo(() => {
		const index = ceil(activeRating - 1);
		return helpTexts && index < helpTexts.length
			? `${helpTexts[index]} ${activeRating}`
			: null;
	}, [activeRating, helpTexts]);

	return (
		<div className={classnames('star-input', size, className)}>
			<span
				className={classnames(
					'ss-label ss-star-input-label',
					className,
					{ error },
				)}
			>
				<Text className="ss-label-text">{label}</Text>
				<div className="input-container">
					{Children.toArray(
						stars.map((value) => (
							<StarButton
								className={classnames(size, {
									half: halfStep,
									active: activeRating >= value,
								})}
								onMouseEnter={onMouseEnter}
								onMouseLeave={onMouseLeave}
								onClick={onSelect}
								value={value}
							/>
						)),
					)}
					{!!helpText && <div className="help-text">{helpText}</div>}
				</div>
				{error && (
					<Paragraph className="error-message">{error}</Paragraph>
				)}
			</span>
		</div>
	);
}

StarInput.propTypes = {
	className: PropTypes.string,
	halfStep: PropTypes.bool,
	input: PropTypes.shape({
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		onChange: PropTypes.func,
	}),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func,
	size: PropTypes.string,
	helpTexts: PropTypes.arrayOf(PropTypes.string),
	label: PropTypes.string,
	error: PropTypes.string,
	totalStars: PropTypes.number,
};

export default StarInput;
